import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { notification } from 'antd';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { dProgramModalAction } from '../../../store/ui';
import { childAction } from '../../../store/program';
import {
    counselorlist,
    getDistrictsService,
    dProgramDetail,
    dProgramCreate,
    dProgramEdit,
} from '../../../services/directorServices';
import { getCampDetailService } from '../../../services/userServices';
import { resHandle, addBr, getBr, userTimezone } from '../../../helper';
import { ProgramFormProvider, useProgramForm } from '../../../context/ProgramFormContext';

import { s, brand, gray, ink, green, white, radius, shadow, fontStack } from './theme';
import { ScrollContainerContext } from './scrollContainer';
import { FIELD_LABELS } from './fields';
import SectionNav from './SectionNav';
import Description from './sections/Description';
import Groups from './sections/Groups';
import Price from './sections/Price';
import Timing from './sections/Timing';
import Location from './sections/Location';
import Amenities from './sections/Amenities';
import Extra from './sections/Extra';
import './programForm.css';

/**
 * Program entry form — the redesigned replacement for `modals/AddProgramModal.js`.
 *
 * Same fields, same gates, same payload; new layout. Opens as a modal from the same
 * `dProgramModalAction` the old form used, swapped in at `modals/index.js` behind the
 * `USE_NEW_UI` flag. The old modal is untouched, so turning the flag off restores it.
 *
 * Mounted only while open (`modals/index.js` renders it conditionally), so each open
 * starts from fresh state — the same lifecycle the old modal had.
 */

const PAGE_BG = '#F7F6F5';

const findMaxMin = arr => {
    let max = arr[0];
    let min = arr[0];
    for (let i = 0; i < arr.length; i++) {
        if (arr[i] > max) {
            max = arr[i];
        } else if (arr[i] < min) {
            min = arr[i];
        }
    }
    return { min, max };
};

const ProgramFormPage = props => {
    // The id the modal was opened with — '' for create, a program id for edit.
    const programId = props.dProgramId;
    const isEdit = Boolean(programId);
    const scrollRef = useRef(null);

    const {
        userInput,
        setUserInput,
        byDates,
        setByDates,
        additionalDates,
        setAdditionalDates,
        cmQuestions,
        setCmQuestions,
        district,
        setDistrict,
        districtList,
        setDistrictList,
        setCounselorList,
        setCampId,
        campId,
        disableOvernight,
        setDisableOvernight,
        overall,
        requiredOnly,
        setAllSections,
        openSection,
        validate,
        issues,
        btnLoder,
        setBtnLoder,
        dirty,
        setDirty,
        markPristine,
        seedRichText,
        resolveRichText,
    } = useProgramForm();

    const [status, setStatus] = useState(null);

    // No permission check here. As a modal it can only be opened by the Add button and the
    // pencil icon, which are themselves gated on UserType and the createProgram /
    // editProgram permissions — exactly as the old form worked. (A route would have needed
    // its own guard, since a hidden button protects nothing once a URL exists.)

    // ---- Reference data ------------------------------------------------------
    useEffect(() => {
        getCampDetailService(`?campCode=${props?.userProfile?.campCode}`).then(res => {
            if (res?.data?.statusCode) {
                setCampId(res.data.responseData.result?._id);
            }
        });
    }, [props?.userProfile?.campCode, setCampId]);

    useEffect(() => {
        getDistrictsService('').then(res => {
            const { status: ok, data } = resHandle(res);
            if (ok) {
                setDistrictList(data.result.map(i => ({ ...i, label: i.name, value: i._id })) || []);
            }
        });
    }, [setDistrictList]);

    useEffect(() => {
        counselorlist('').then(res => {
            if (res?.data?.statusCode) {
                setCounselorList(res.data.responseData.result.counselor);
            }
        });
    }, [setCounselorList]);

    // ---- Edit hydration ------------------------------------------------------
    // Ported from `AddProgramModal.js:1149-1240`. The field-by-field mapping and its
    // fallbacks are load-bearing; they are copied rather than tidied.
    useEffect(() => {
        if (!programId) return;

        dProgramDetail(`?programId=${programId}`).then(res => {
            const { status: ok, data } = resHandle(res);
            if (!ok) return;

            setUserInput({
                programId: data.result._id,
                programName: data.result.programName,
                programCode: data.result.programCode,
                noOfSlots: data.result.noOfSlots,
                registerNonRefundFee:
                    data.result.registerNonRefundFee || data.result.registerNonRefundFee === 0
                        ? data.result.registerNonRefundFee
                        : '',
                dateEarlyRegisterOpen: data.result.dateEarlyRegisterOpen
                    ? data.result.dateEarlyRegisterOpen
                    : '',
                dateEarlyRegisterClosed: data.result.dateEarlyRegisterClosed
                    ? data.result.dateEarlyRegisterClosed
                    : '',
                earlyBirdPrice: data.result.earlyBirdPrice ? data.result.earlyBirdPrice : '',
                registerOpenDate: data.result.registerOpenDate,
                registerCloseDate: data.result.registerCloseDate,
                registrationFee: data.result.registrationFee,
                commuterPrice: data.result.commuterPrice || '',
                // Absent on programs that never set them, which the server reads as
                // "inherit commuterPrice". Blank keeps that meaning on a round-trip.
                earlyBirdCommuterPrice: data.result.earlyBirdCommuterPrice || '',
                lateCommuterPrice: data.result.lateCommuterPrice || '',
                dateLateRegisterOpen: data.result.dateLateRegisterOpen
                    ? data.result.dateLateRegisterOpen
                    : '',
                dateLateRegisterClosed: data.result.dateLateRegisterClosed
                    ? data.result.dateLateRegisterClosed
                    : '',
                lateRegistrationFee: data.result.lateRegistrationFee
                    ? data.result.lateRegistrationFee
                    : '',
                category: data.result.categoryName,
                subCategory: data?.result?.subCategory?.map(i => i._id) || [],
                addressLine1: data.result.addressLine1,
                city: data.result.city,
                state: data.result.state,
                zipCode: data.result.zipCode,
                gender: data.result.gender,
                programType: data.result.programType,
                minAge: data.result.minAge,
                maxAge: data.result.maxAge,
                grade: data.result.grade,
                expLevel: data.result.expLevel,
                startDate: data.result.startDate,
                endDate: data.result.endDate,
                startDateFormatted: data.result.startDateFormatted || '',
                endDateFormatted: data.result.endDateFormatted || '',
                checkInTime: data.result.checkInTime,
                checkOutTime: data.result.checkOutTime,
                attendanceHour: data.result.attendanceHour,
                meals: data.result.meals,
                tshirt: data.result.tShirt,
                equipment: data.result.equipment,
                facility: data.result.facility,
                travel: data.result.travel,
                travelInfo: data?.result?.travelInfo ? getBr(data.result.travelInfo) : '',
                scholarships: data.result.scholarships,
                scholarshipsUrl: data.result.scholarshipsUrl,
                discount: data.result.discount,
                // The five rich-text fields are HTML, edited by ReactQuill and rendered
                // with dangerouslySetInnerHTML/getHtml everywhere they are displayed, so
                // they load and save verbatim. They used to be piped through
                // getBr()/addBr() — a plain-textarea-era pair that turns <br> into "\n"
                // and back. Running that either side of Quill is lossy: Quill re-parses
                // the "\n" as HTML whitespace and collapses it, so every save flattened
                // the description a little further and logged an audit entry for a change
                // the director never made. travelInfo below is still a real textarea and
                // still needs the pair.
                impNotes: data.result.impNotes || '',
                programIntroduction: data.result.programIntroduction || '',
                aboutProgram: data.result.aboutProgram || '',
                programInstruction: data.result.programInstruction || '',
                disclaimer: data.result.disclaimer || '',
                pdf: data.result.pdf || '',
                lat: data.result.geoLocation ? data.result.geoLocation[1] : '',
                long: data.result.geoLocation ? data.result.geoLocation[0] : '',
                isfeatured: data.result.isfeatured,
                isprivate: data.result.isprivate,
                discountType: data.result.discountType ? data.result.discountType : 1,
                siblingDiscount: data.result.siblingDiscount,
                siblingDiscountType: data.result.siblingDiscountType,
                multiProgramDiscount: data.result.multiProgramDiscount,
                multiProgramDiscountType: data.result.multiProgramDiscountType,
                isOnline: data.result.isOnline,
                tax: data.result.tax,
                counselors: data.result.counselors,
                earlyConvenienceFee: data.result.earlyConvenienceFee
                    ? data.result.earlyConvenienceFee
                    : '',
                convenienceFee: data.result.convenienceFee ? data.result.convenienceFee : '',
                lateConvenienceFee: data.result.lateConvenienceFee
                    ? data.result.lateConvenienceFee
                    : '',
                programTimezone: data.result?.programTimezone
                    ? data.result.programTimezone
                    : userTimezone,
                location: `${data.result.addressLine1} ${data.result.city} ${data.result.state} ${data.result.addressLine1}`,
            });

            setDisableOvernight(data.result?.disableOvernight ? 1 : 0);
            setByDates(data.result?.byDates === 1 ? 1 : 2);
            setAdditionalDates(data.result?.additionalDates || []);
            setCmQuestions(
                data?.result?.questions?.length
                    ? data.result.questions.map(ques => {
                        !ques?.questionId && (ques.questionId = uuidv4());
                        return ques;
                    })
                    : []
            );

            if (data?.result?.districtId) {
                const tempDistrict = districtList.find(i => i._id === data?.result?.districtId);
                if (tempDistrict) {
                    setDistrict({
                        ...tempDistrict,
                        value: tempDistrict._id,
                        label: tempDistrict.name,
                    });
                }
            }

            // Hydration is not a user edit. `setDirty(false)` alone lost the race — it runs
            // before the tracking effect commits, which then flipped it straight back.
            markPristine();

            // Remember the rich text exactly as the API sent it. ReactQuill will rewrite
            // these in state the moment it mounts — same markup, inline styles re-ordered —
            // and submit posts these originals back for any editor nobody typed in.
            seedRichText({
                impNotes: data.result.impNotes || '',
                programIntroduction: data.result.programIntroduction || '',
                aboutProgram: data.result.aboutProgram || '',
                programInstruction: data.result.programInstruction || '',
                disclaimer: data.result.disclaimer || '',
            });
        });
        // districtList is read but intentionally not a dependency — refetching the whole
        // program when the district list arrives would clobber in-progress edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programId]);

    // ---- Submit --------------------------------------------------------------
    // The FormData block below is `AddProgramModal.js:1022-1143` verbatim. Its `'NaN'`
    // string checks and conditional blanking encode years of edge cases — do not tidy.
    const handleSubmit = e => {
        e.preventDefault();

        const { valid, missing } = validate();

        if (!valid || missing.length > 0) {
            const blocking = issues.filter(issue => issue.severity === 'error');
            setStatus({ kind: 'blocked', fields: missing, issues: blocking });
            setAllSections(true);

            const target = missing[0] || blocking[0];
            if (target) {
                if (target.section) openSection(target.section);
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        const el = document.getElementById(target.field || target.section);
                        if (!el) return;
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        if (typeof el.focus === 'function') el.focus({ preventScroll: true });
                    });
                });
            }
            return;
        }

        setStatus(null);
        setBtnLoder(true);

        const {
            programName,
            programCode,
            noOfSlots,
            registerNonRefundFee,
            earlyBirdPrice,
            registerOpenDate,
            registerCloseDate,
            registrationFee,
            commuterPrice,
            earlyBirdCommuterPrice,
            lateCommuterPrice,
            dateLateRegisterOpen,
            dateLateRegisterClosed,
            lateRegistrationFee,
            category,
            subCategory,
            addressLine1,
            city,
            state,
            zipCode,
            gender,
            programType,
            minAge,
            maxAge,
            grade,
            expLevel,
            startDate,
            endDate,
            checkInTime,
            checkOutTime,
            attendanceHour,
            meals,
            tshirt,
            equipment,
            facility,
            travel,
            travelInfo,
            scholarships,
            discount,
            impNotes,
            programIntroduction,
            aboutProgram,
            programInstruction,
            disclaimer,
            lat,
            long,
            scholarshipsUrl,
            isfeatured,
            isprivate,
            dateEarlyRegisterOpen,
            dateEarlyRegisterClosed,
            discountType,
            isOnline,
            tax,
            earlyConvenienceFee,
            convenienceFee,
            lateConvenienceFee,
            counselors,
            siblingDiscount,
            siblingDiscountType,
            multiProgramDiscount,
            multiProgramDiscountType,
            programTimezone,
        } = userInput;

        const params = new FormData();
        // Appended raw, exactly as the old modal does. On create `dProgramId` is
        // `undefined` — DirectorCamp.js:662 declares its dispatch prop with no parameter,
        // so the `''` the Add button passes is discarded — and FormData stringifies that
        // to the literal "undefined". The API's `programId != "undefined"` guard
        // (mediaUpload.js:171) depends on receiving that exact string: anything else
        // non-null reaches `ObjectId()`, which throws on `''` and kills the process.
        params.append('programId', programId);
        params.append('campId', campId);
        params.append('programName', programName);
        params.append('programCode', programCode);
        params.append('registerNonRefundFee', (registerNonRefundFee || registerNonRefundFee === 0) ? registerNonRefundFee : '');
        params.append('dateEarlyRegisterOpen', dateEarlyRegisterClosed ? ((dateEarlyRegisterOpen === 'null' || dateEarlyRegisterOpen === '' || dateEarlyRegisterOpen === 'NaN') ? '' : dateEarlyRegisterOpen) : '');
        params.append('dateEarlyRegisterClosed', dateEarlyRegisterOpen === '' ? '' : (dateEarlyRegisterClosed === 'null' || dateEarlyRegisterClosed === '' || dateEarlyRegisterClosed === 'NaN') ? '' : dateEarlyRegisterClosed);
        params.append('earlyBirdPrice', (earlyBirdPrice === '' || earlyBirdPrice === 'NaN' || earlyBirdPrice === null) ? '' : earlyBirdPrice);
        params.append('registerOpenDate', registerOpenDate);
        params.append('registerCloseDate', registerCloseDate);
        params.append('registrationFee', registrationFee);
        params.append('commuterPrice', commuterPrice || 0);
        // 0 means "inherit commuterPrice" for these two, which is what a blank box means
        // here. Sent rather than omitted so clearing one actually clears it server-side.
        params.append('earlyBirdCommuterPrice', earlyBirdCommuterPrice || 0);
        params.append('lateCommuterPrice', lateCommuterPrice || 0);
        params.append('dateLateRegisterOpen', dateLateRegisterClosed ? ((dateLateRegisterOpen === 'null' || dateLateRegisterOpen === '' || dateLateRegisterOpen === 'NaN') ? '' : dateLateRegisterOpen) : '');
        params.append('dateLateRegisterClosed', (dateLateRegisterClosed === '' || dateLateRegisterClosed === '' || dateLateRegisterClosed === 'NaN') ? '' : dateLateRegisterClosed);
        params.append('lateRegistrationFee', dateLateRegisterOpen === '' ? '' : (lateRegistrationFee === '' || lateRegistrationFee === 'NaN' || lateRegistrationFee === null) ? '' : parseInt(lateRegistrationFee));
        params.append('category', category);
        params.append('subCategory', JSON.stringify(subCategory));
        params.append('addressLine1', addressLine1);
        params.append('city', city);
        params.append('state', state);
        params.append('zipCode', zipCode);
        params.append('gender', gender);
        params.append('campCode', props.userProfile.campCode);
        params.append('programType', programType);
        params.append('minAge', minAge);
        params.append('maxAge', maxAge);
        params.append('grade', JSON.stringify(grade));
        params.append('expLevel', expLevel);
        params.append('checkInTime', checkInTime);
        params.append('checkOutTime', checkOutTime);
        params.append('attendanceHour', attendanceHour);
        params.append('meals', meals ? JSON.stringify(meals) : '[]');
        params.append('tshirt', tshirt);
        params.append('equipment', equipment);
        params.append('facility', parseInt(facility));
        params.append('travel', parseInt(travel));
        params.append('travelInfo', addBr(travelInfo));
        params.append('scholarships', scholarships);
        params.append('discount', discount ? parseInt(discount) : 0);
        // resolveRichText, not the raw state: an editor the director never typed in posts
        // back the API's own markup rather than ReactQuill's re-ordered re-render of it.
        // See the refs in ProgramFormContext for why those two differ.
        params.append('impNotes', resolveRichText('impNotes', impNotes));
        params.append('questions', JSON.stringify(cmQuestions));
        params.append('programIntroduction', resolveRichText('programIntroduction', programIntroduction));
        params.append('aboutProgram', resolveRichText('aboutProgram', aboutProgram));
        params.append('programInstruction', resolveRichText('programInstruction', programInstruction));
        params.append('disclaimer', resolveRichText('disclaimer', disclaimer));
        params.append('lat', lat);
        params.append('long', long);
        params.append('scholarshipsUrl', scholarshipsUrl);
        params.append('isfeatured', isfeatured ? parseInt(isfeatured) : 0);
        params.append('isprivate', isprivate ? parseInt(isprivate) : 0);
        params.append('discountType', discountType);
        params.append('isOnline', isOnline);
        params.append('tax', tax ? tax : 0);
        params.append('earlyConvenienceFee', earlyConvenienceFee);
        params.append('convenienceFee', convenienceFee ? convenienceFee : '');
        params.append('lateConvenienceFee', lateConvenienceFee);
        params.append('siblingDiscount', siblingDiscount ? siblingDiscount : 0);
        params.append('siblingDiscountType', siblingDiscountType ? siblingDiscountType : 1);
        params.append('multiProgramDiscount', multiProgramDiscount);
        params.append('multiProgramDiscountType', multiProgramDiscountType);
        params.append('programTimezone', programTimezone);
        params.append('disableOvernight', disableOvernight === 1 ? true : false);

        if (counselors.length) {
            params.append('counselors', JSON.stringify(counselors));
        } else {
            params.append('counselors', JSON.stringify([]));
        }

        if (district.value) {
            //params.append('districtId', district.value);
        }

        params.append('additionalDates', JSON.stringify(additionalDates));
        params.append('byDates', byDates);

        if (byDates === 1) {
            const all_additionalDates = [];
            for (let i = 0; i < additionalDates.length; i++) {
                all_additionalDates.push(additionalDates[i].startDate);
                all_additionalDates.push(additionalDates[i].endDate);
            }
            const { min, max } = findMaxMin(all_additionalDates);
            params.append('startDate', min);
            params.append('endDate', max);
        } else {
            params.append('startDate', startDate);
            params.append('endDate', endDate);
            params.append('noOfSlots', parseInt(noOfSlots));
        }

        const done = message => {
            setDirty(false);
            closeModal();
            props.childAction();
            notification.open({ message: 'Program', description: message });
        };

        if (programId) {
            dProgramEdit(params).then(ok => {
                setBtnLoder(false);
                if (ok) done('Program updated successfully.');
            });
        } else {
            dProgramCreate(params).then(ok => {
                setBtnLoder(false);
                if (ok) done('Program created successfully.');
            });
        }
    };

    // Unmounting clears the form state, so there is nothing to reset here — the old modal
    // had to blank ~70 fields by hand because it stayed mounted.
    const closeModal = () => {
        setDirty(false);
        props.dProgramModalAction('');
    };

    const handleCancel = () => {
        if (dirty && !window.confirm('You have unsaved changes. Close without saving?')) return;
        closeModal();
    };

    const blockedCount = status && status.kind === 'blocked'
        ? status.fields.length + status.issues.length
        : 0;

    return (
        <Modal
            show={props.dProgramModal}
            onHide={handleCancel}
            size="lg"
            animation={false}
            scrollable
            dialogClassName="program_form_modal"
            centered
        >
            {/* Header sits outside the scroll area, so it stays put without `sticky`. */}
            <Modal.Header
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '14px 20px',
                    borderBottom: `1px solid ${gray[200]}`,
                    backgroundColor: white,
                    fontFamily: fontStack,
                }}
            >
                <div>
                    <p
                        style={{
                            margin: 0,
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: brand[600],
                        }}
                    >
                        CampsEZ · Programs
                    </p>
                    <h4
                        style={{
                            margin: 0,
                            fontSize: '20px',
                            fontWeight: 700,
                            letterSpacing: '-0.01em',
                            color: ink[900],
                        }}
                    >
                        {isEdit ? 'Edit Program' : 'Add Program'}
                    </h4>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                        className="d-none d-sm-inline"
                        style={{ fontSize: '12px', fontWeight: 500, color: ink[500] }}
                    >
                        {overall.filled}/{overall.total} required complete
                    </span>
                    <button type="button" style={s.buttonSecondary} onClick={handleCancel}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="program-form"
                        disabled={btnLoder}
                        style={btnLoder ? { ...s.buttonPrimary, ...s.buttonDisabled } : s.buttonPrimary}
                    >
                        {btnLoder ? 'Saving…' : 'Save Program'}
                    </button>
                </div>
            </Modal.Header>

            <Modal.Body
                ref={scrollRef}
                className="program-form"
                style={{ backgroundColor: PAGE_BG, padding: '20px', fontFamily: fontStack }}
            >
                <ScrollContainerContext.Provider value={scrollRef}>
                {status ? (
                    <div
                        role="status"
                        style={{
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            borderRadius: radius.lg,
                            border: `1px solid ${status.kind === 'saved' ? green[100] : brand[200]}`,
                            backgroundColor: status.kind === 'saved' ? green[100] : brand[50],
                            padding: '12px 16px',
                        }}
                    >
                        {status.kind === 'saved' ? (
                            <CheckCircle2 size={16} color={green[600]} style={{ marginTop: '2px', flexShrink: 0 }} aria-hidden="true" />
                        ) : (
                            <AlertTriangle size={16} color={brand[600]} style={{ marginTop: '2px', flexShrink: 0 }} aria-hidden="true" />
                        )}

                        <div style={{ minWidth: 0, flex: 1, fontSize: '13px', lineHeight: 1.6 }}>
                            <p style={{ margin: 0, fontWeight: 600, color: brand[800] }}>
                                Fix {blockedCount} item{blockedCount === 1 ? '' : 's'} before saving
                            </p>
                            {status.fields.length > 0 ? (
                                <p style={{ margin: '2px 0 0', color: brand[700] }}>
                                    Missing:{' '}
                                    {status.fields
                                        .slice(0, 5)
                                        .map(item => FIELD_LABELS[item.field] || item.field)
                                        .join(', ')}
                                    {status.fields.length > 5
                                        ? ` +${status.fields.length - 5} more`
                                        : ''}
                                </p>
                            ) : ''}
                            {status.issues.length > 0 ? (
                                <ul style={{ margin: '4px 0 0', paddingLeft: '18px', color: brand[700] }}>
                                    {status.issues.slice(0, 4).map(issue => (
                                        <li key={issue.key}>{issue.message}</li>
                                    ))}
                                </ul>
                            ) : ''}
                        </div>

                        <button
                            type="button"
                            onClick={() => setStatus(null)}
                            aria-label="Dismiss message"
                            style={{
                                background: 'none',
                                border: 'none',
                                borderRadius: radius.sm,
                                padding: '4px',
                                color: ink[400],
                                cursor: 'pointer',
                            }}
                        >
                            <X size={16} aria-hidden="true" />
                        </button>
                    </div>
                ) : ''}

                <div className="row">
                    <div className="col-lg-3 mb-4">
                        <SectionNav />
                    </div>
                    <div className="col-lg-9">
                        <form id="program-form" onSubmit={handleSubmit} noValidate>
                            <Description {...props} />
                            <Groups />
                            <Price />
                            <Timing />
                            <Location />
                            <Amenities />
                            {!requiredOnly ? <Extra /> : ''}

                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    marginTop: '20px',
                                    borderRadius: radius.xl,
                                    border: `1px solid ${gray[200]}`,
                                    backgroundColor: white,
                                    padding: '16px 20px',
                                    boxShadow: shadow.card,
                                }}
                            >
                                <p style={{ margin: 0, fontSize: '12.5px', color: ink[500] }}>
                                    Required fields are marked with{' '}
                                    <span style={{ fontWeight: 600, color: brand[600] }}>*</span>.
                                </p>
                                <button
                                    type="submit"
                                    disabled={btnLoder}
                                    style={btnLoder ? { ...s.buttonPrimary, ...s.buttonDisabled } : s.buttonPrimary}
                                >
                                    {btnLoder ? 'Saving…' : 'Save Program'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                </ScrollContainerContext.Provider>
            </Modal.Body>
        </Modal>
    );
};

/**
 * The provider has to sit outside the form so the form can call `useProgramForm`.
 */
const ProgramForm = props => (
    <ProgramFormProvider>
        <ProgramFormPage {...props} />
    </ProgramFormProvider>
);

const mapStateToProps = state => {
    const { userProfile } = state.user;
    const { dProgramModal, dProgramId } = state.ui;
    const { category, subcategory } = state.program.preList || {};

    return {
        dProgramModal,
        dProgramId,
        category: category || [],
        subcategories: subcategory || [],
        userProfile,
    };
};

const mapDispatchToProps = dispatch => ({
    dProgramModalAction: params => dispatch(dProgramModalAction(params)),
    childAction: () => dispatch(childAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProgramForm);
